import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { BaseSheet } from "@/components/common/BaseSheet";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";

type MemoryRow = Tables<"account_memory">;

interface GenieMemorySheetProps {
  isOpen: boolean;
  onClose: () => void;
  genieId: string;
}

const memoryQueryKey = (genieId: string) => ["genie-memory", genieId];

/**
 * Lets a genie owner see what their genie remembers about the people it has
 * talked to, and forget individual memories one at a time (rather than only
 * being able to switch memory off entirely, which drops everything).
 *
 * Reads/deletes go straight through the browser Supabase client, relying on
 * RLS to scope rows to genies this owner actually owns — same pattern
 * `CallLogs.tsx` uses for call_logs.
 */
export const GenieMemorySheet = ({ isOpen, onClose, genieId }: GenieMemorySheetProps) => {
  const queryClient = useQueryClient();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data: memories, isLoading, error } = useQuery({
    queryKey: memoryQueryKey(genieId),
    queryFn: async (): Promise<MemoryRow[]> => {
      const { data, error } = await supabase
        .from("account_memory")
        .select("*")
        .eq("genie_id", genieId)
        .eq("scope", "genie")
        .or("expires_at.is.null,expires_at.gt." + new Date().toISOString())
        .order("importance", { ascending: false })
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data ?? [];
    },
    enabled: isOpen && !!genieId,
  });

  const handleForget = async (id: string) => {
    setDeletingId(id);
    try {
      const { error } = await supabase.from("account_memory").delete().eq("id", id);
      if (error) throw error;
      queryClient.setQueryData<MemoryRow[]>(memoryQueryKey(genieId), (rows) =>
        (rows ?? []).filter((row) => row.id !== id),
      );
      toast.success("Memory forgotten");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Couldn't forget that memory";
      toast.error(message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <BaseSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Genie Memory"
      description="What this genie remembers from past conversations, and with whom."
      size="lg"
      loading={isLoading}
      error={error ? (error as Error).message : null}
    >
      {!memories || memories.length === 0 ? (
        <div className="text-center py-10 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
          <GenieIcon name="brain" size={40} color="#4E9CFF" secondaryColor="#FF4DB8" gradientEnabled className="mb-3 opacity-70" />
          <p className="text-sm text-gray-500">Nothing remembered yet</p>
          <p className="text-xs text-gray-400 mt-1">Memories appear here after conversations are analyzed</p>
        </div>
      ) : (
        <div className="space-y-3">
          {memories.map((memory) => (
            <div
              key={memory.id}
              className="p-4 bg-white border border-gray-200 rounded-xl flex items-start justify-between gap-3"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-700 whitespace-pre-line break-words">{memory.content}</p>
                <p className="text-xs text-gray-400 mt-2">
                  {new Date(memory.created_at).toLocaleDateString()}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleForget(memory.id)}
                disabled={deletingId === memory.id}
                aria-label="Forget this memory"
                className="flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </BaseSheet>
  );
};
